/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { ArrowLeft, Share2, Database, Brain, Copy, Check, Lock, Wallet, Loader2, Key, Shield } from 'lucide-react';
import { useWallet } from '../providers/WalletProvider';
import { TREASURY_WALLET_SOL } from '../lib/constants';

interface ApiAccessProps {
    onBack: () => void;
    onConnectWallet?: () => void;
}

const swaggerContent = `
openapi: 3.0.0
info:
  title: HOT Network API
  description: API for minting tokens, executing trades, and accessing AI market intelligence on Solana and Sui.
  version: 1.1.0
servers:
  - url: https://api.hotnetwork.app/v1
    description: Main Production Server
paths:
  /auth/login:
    post:
      summary: Authenticate wallet
      description: Returns a JWT session token.
      responses:
        '200':
          description: Session created
  /tokens/mint:
    post:
      summary: Mint a new token
      description: Deploys a new SPL or Sui Coin to the network.
      requestBody:
        required: true
        content:
          application/json:
            schema:
              $ref: '#/components/schemas/TokenConfig'
      responses:
        '200':
          description: Successful mint
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/MintResult'
  /tokens:
    get:
      summary: List Tokens
      description: Get a paginated list of tokens. Filters devnet tokens by default in production.
      parameters:
        - in: query
          name: network
          schema:
            type: string
            enum: [mainnet, devnet]
          description: Filter tokens by network environment.
      responses:
        '200':
          description: List of tokens
          content:
            application/json:
              schema:
                type: array
                items:
                  $ref: '#/components/schemas/TokenMetadata'
  /trade/swap:
    post:
      summary: Execute a trade
      description: Swaps tokens via NovaDEX liquidity pools.
      requestBody:
        required: true
        content:
          application/json:
            schema:
              $ref: '#/components/schemas/TradeRequest'
      responses:
        '200':
          description: Trade executed
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/TradeResult'
  /analytics/signals:
    get:
      summary: Get AI Trading Signals
      description: Returns active market opportunities detected by the Swarm Neural Engine.
      responses:
        '200':
          description: List of signals
          content:
            application/json:
              schema:
                type: array
                items:
                  $ref: '#/components/schemas/AiSignal'
  /analytics/smart-money:
    get:
      summary: Get Smart Money Activity
      description: Returns recent trades from tracked high-performance wallets.
      responses:
        '200':
          description: List of trader activity
          content:
            application/json:
              schema:
                type: array
                items:
                  $ref: '#/components/schemas/SmartMoneyTrader'
  /ai/agent/settings:
    get:
      summary: Get AI Agent Settings
      responses:
        '200':
          description: User agent configuration
          content:
            application/json:
              schema:
                $ref: '#/components/schemas/AgentSettings'
    post:
      summary: Update AI Agent Settings
      requestBody:
        required: true
        content:
          application/json:
            schema:
              $ref: '#/components/schemas/AgentSettings'
      responses:
        '200':
          description: Settings updated
components:
  schemas:
    TokenConfig:
      type: object
      required:
        - name
        - ticker
        - supply
        - chain
        - network
      properties:
        name:
          type: string
        ticker:
          type: string
        supply:
          type: number
        chain:
          type: string
          enum: [SOL, SUI]
        network:
          type: string
          enum: [mainnet, devnet, testnet]
        tokenStandard:
          type: string
          enum: [SPL, TOKEN_2022, SUI_COIN]
        extensions:
          type: object
          properties:
            transferFeeBasisPoints:
              type: number
            interestRate:
              type: number
            revokeMint:
              type: boolean
            immutable:
              type: boolean
    MintResult:
      type: object
      properties:
        txHash:
          type: string
        mintAddress:
          type: string
        status:
          type: string
        network:
          type: string
    TokenMetadata:
      type: object
      properties:
        name:
          type: string
        symbol:
          type: string
        mintAddress:
          type: string
        network:
          type: string
    TradeRequest:
      type: object
      properties:
        tokenAddress:
          type: string
        amountIn:
          type: number
        side:
          type: string
          enum: [BUY, SELL]
        network:
          type: string
    TradeResult:
      type: object
      properties:
        txHash:
          type: string
        amountOut:
          type: number
    AiSignal:
      type: object
      properties:
        id:
          type: string
        tokenSymbol:
          type: string
        type:
          type: string
          enum: [BUY, SELL]
        confidence:
          type: number
        reasoning:
          type: string
        timestamp:
          type: number
    SmartMoneyTrader:
      type: object
      properties:
        address:
          type: string
        pnl:
          type: number
        winRate:
          type: number
        badges:
          type: array
          items: 
            type: string
    AgentSettings:
      type: object
      properties:
        riskLevel:
          type: string
          enum: [Conservative, Balanced, Aggressive]
        tradeSize:
          type: number
        maxSlippage:
          type: number
        dailyDrawdown:
          type: number
        profitTarget:
          type: number
`.trim();

export const ApiAccess: React.FC<ApiAccessProps> = ({ onBack, onConnectWallet }) => {
    const { isConnected, address } = useWallet();
    const [isCopied, setIsCopied] = useState(false);
    const [hasAccess, setHasAccess] = useState(false);
    const [isBuying, setIsBuying] = useState(false);

    // Check local storage for previous purchase
    useEffect(() => {
        const storedAccess = localStorage.getItem('hot_api_unlocked');
        if (storedAccess === 'true') {
            setHasAccess(true);
        }
    }, []);

    const handleCopy = () => {
        navigator.clipboard.writeText(swaggerContent);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
    };

    const handleUnlock = async () => {
        if (!isConnected) {
            if (onConnectWallet) onConnectWallet();
            return;
        }

        setIsBuying(true);

        try {
            // Simulate Payment Transaction
            // In a real app, this would use TransactionService.transfer(address, TREASURY_WALLET_SOL, 0.5)
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            // Success
            localStorage.setItem('hot_api_unlocked', 'true');
            setHasAccess(true);
        } catch (e) {
            console.error("Payment failed", e);
            alert("Payment simulation failed. Please try again.");
        } finally {
            setIsBuying(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#F9F8F4] flex flex-col relative animate-fade-in">
            <main className="flex-1 container mx-auto px-6 pb-24">

                <div className="flex items-center gap-4 pt-8 pb-12">
                    <button onClick={onBack} className="p-2 hover:bg-stone-200 rounded-full text-stone-600 transition-colors bg-white shadow-sm border border-stone-200">
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h2 className="font-serif text-3xl font-bold text-stone-900">API Access & Integrations</h2>
                        <p className="text-sm text-stone-500">Explore the infrastructure powering HotLaunchpad.</p>
                    </div>
                </div>

                <div className="prose prose-stone max-w-none">
                    <p>
                        HotLaunchpad is built on a robust stack of decentralized and centralized services to provide real-time data, AI analysis, and reliable transaction processing. Below is an overview of our core APIs.
                    </p>
                </div>
                
                <div className="my-12">
                    <h3 className="font-serif text-2xl text-stone-900 mb-6">Core Infrastructure</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 not-prose">
                        {[
                            { icon: <Share2 size={20}/>, title: 'RPC Providers (Helius)', desc: 'For high-performance access to Solana and Sui blockchain data, including transaction history and account states.' },
                            { icon: <Database size={20}/>, title: 'Decentralized Storage (IPFS)', desc: 'All token metadata and media assets are pinned to IPFS via Pinata for permanent, decentralized availability.' },
                            { icon: <Brain size={20}/>, title: 'AI Engine (Google Gemini)', desc: 'Our AI assistant and strategy generator is powered by Google\'s Gemini API for market analysis.' },
                        ].map(item => (
                             <div key={item.title} className="p-6 bg-white rounded-2xl border border-stone-200 shadow-sm hover:shadow-md transition-shadow">
                                <div className="w-10 h-10 bg-stone-100 rounded-xl flex items-center justify-center mb-4 text-stone-600">
                                    {item.icon}
                                </div>
                                <h4 className="font-bold text-stone-900 mb-1">{item.title}</h4>
                                <p className="text-xs text-stone-500 leading-relaxed">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>

                <div>
                    <h3 className="font-serif text-2xl text-stone-900 mb-2">Developer API Specification</h3>
                    <p className="prose prose-stone max-w-none mb-6">
                        Access the full OpenAPI v3.0 specification for minting, trading, and analytics integration.
                    </p>
                </div>
                
                {hasAccess ? (
                    <div className="mt-4 bg-[#1e1e1e] rounded-2xl border border-stone-800 shadow-xl animate-fade-in-up overflow-hidden">
                        <div className="bg-[#252526] px-4 py-3 border-b border-black/20 flex justify-between items-center">
                            <div className="flex items-center gap-3">
                                <div className="p-1.5 bg-green-500/10 rounded text-green-500"><Key size={14} /></div>
                                <span className="text-xs font-mono font-medium text-stone-400">hot-network-api-v1.yaml</span>
                            </div>
                            <button 
                                onClick={handleCopy}
                                className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold rounded-lg bg-[#333333] hover:bg-[#3e3e3e] text-stone-300 transition-all border border-white/5 hover:border-white/10"
                            >
                                {isCopied ? <><Check size={12}/> Copied</> : <><Copy size={12}/> Copy Spec</>}
                            </button>
                        </div>
                        <div className="p-0 overflow-hidden">
                            <div className="max-h-[600px] overflow-auto custom-scrollbar p-6 bg-[#1e1e1e]">
                                <pre className="text-xs md:text-sm text-[#d4d4d4] font-mono leading-relaxed whitespace-pre font-medium">
                                    <code>{swaggerContent}</code>
                                </pre>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="mt-6 relative w-full rounded-3xl overflow-hidden border border-stone-200 shadow-2xl group bg-stone-900">
                        {/* Dynamic Background Pattern */}
                        <div className="absolute inset-0 opacity-20 pointer-events-none">
                            <div className="absolute top-0 left-0 w-full h-full bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
                            <div className="absolute top-0 right-0 w-64 h-64 bg-nobel-gold/20 rounded-full blur-[100px]"></div>
                            <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/10 rounded-full blur-[100px]"></div>
                        </div>

                        {/* Content Container (Relative ensures height adapts) */}
                        <div className="relative z-10 flex flex-col items-center justify-center p-10 md:p-16 text-center">
                            
                            <div className="w-20 h-20 bg-gradient-to-br from-stone-800 to-stone-900 rounded-2xl flex items-center justify-center text-nobel-gold mb-8 shadow-2xl border border-stone-700/50 relative group-hover:scale-105 transition-transform duration-500">
                                <div className="absolute inset-0 bg-nobel-gold/10 blur-xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                <Lock size={36} className="relative z-10 drop-shadow-md" />
                            </div>
                            
                            <h3 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4 tracking-tight">
                                Developer Access Locked
                            </h3>
                            
                            <p className="text-stone-400 max-w-lg mb-10 text-base md:text-lg leading-relaxed font-light">
                                Purchase the full OpenAPI specification license to build custom bots, integrations, and analytics tools on the Hot Network.
                            </p>
                            
                            <div className="flex flex-col gap-4 w-full max-w-xs relative">
                                <button 
                                    onClick={handleUnlock}
                                    disabled={isBuying}
                                    className="w-full py-4 bg-gradient-to-r from-nobel-gold to-[#d4b068] text-stone-900 rounded-xl font-bold text-sm hover:brightness-110 transition-all shadow-[0_0_20px_rgba(197,160,89,0.3)] flex items-center justify-center gap-3 transform active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
                                >
                                    {isBuying ? (
                                        <><Loader2 size={18} className="animate-spin" /> Processing...</>
                                    ) : !isConnected ? (
                                        <><Wallet size={18} /> Connect Wallet to Unlock</>
                                    ) : (
                                        <><Key size={18} /> Unlock Access (0.5 SOL)</>
                                    )}
                                </button>
                                
                                <div className="flex items-center justify-center gap-2 text-[10px] text-stone-500 uppercase tracking-widest font-bold">
                                    <Shield size={12} /> Secure On-Chain Payment
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};